<template>
  <div class="rsel" @click="onRootClick">

    <div class="rsel-field" :class="{ open: listOpen || foOpen }">
      <div class="rsel-tags">
        <span v-for="code in selectedCodes" :key="code" class="tag">
          {{ CODE2NAME[code] }}
          <button type="button" @click.stop="remove(code)">×</button>
        </span>
        <input ref="inp" v-model="q" class="rsel-input" :placeholder="selectedCodes.length ? '' : placeholder"
          @keydown.stop @keydown.backspace="onBackspace" />
      </div>

      <button type="button" class="rsel-fo-btn" @click.stop="toggleFo">
        Выберите страну или округ
      </button>
    </div>

    <div v-if="listOpen" class="rsel-dropdown" @mousedown.prevent @click.stop>
      <div v-if="!filtered.length" class="muted p-2">Ничего не найдено</div>
      <ul v-else class="list">
        <li v-for="opt in filtered" :key="opt.code" @click="toggle(opt.code)">
          <input type="checkbox" :checked="isSelected(opt.code)" />
          <span class="name">{{ opt.name }}</span>
        </li>
      </ul>
    </div>

    <!-- Страны и округа -->
    <div v-if="foOpen" class="rsel-fo" @mousedown.prevent @click.stop>
      <div class="fo-header">
        <strong>Страны и округа</strong>
        <button class="btn-link" @click="clearFo">Сбросить</button>
      </div>

      <ul class="list">
        <!-- ВСЕ -->
        <li class="country">
          <label class="country-label">
            <input type="checkbox" :checked="isAllChecked" @change="toggleAll($event.target.checked)" />
            <strong>Все</strong>
          </label>
        </li>

        <li class="divider" aria-hidden="true"></li>

        <!-- РОССИЯ -->
        <li class="country">
          <label class="country-label">
            <input type="checkbox" :checked="isGroupChecked('ru')" @change="toggleGroup('ru', $event.target.checked)" />
            <strong>Россия</strong>
          </label>
          <button class="chevron" @click="groupOpen.ru = !groupOpen.ru">{{ groupOpen.ru ? '▾' : '▸' }}</button>
        </li>

        <template v-if="groupOpen.ru">
          <li v-for="(names, fo) in DISTRICTS" :key="fo" class="indent">
            <label>
              <input type="checkbox" :checked="isFoChecked(fo)" @change="toggleFoCheck(fo, $event.target.checked)" />
              {{ fo }}
            </label>
          </li>
        </template>

        <!-- КАЗАХСТАН -->
        <li class="country">
          <label class="country-label">
            <input type="checkbox" :checked="isGroupChecked('kz')" @change="toggleGroup('kz', $event.target.checked)" />
            <strong>Казахстан</strong>
          </label>
          <button class="chevron" @click="groupOpen.kz = !groupOpen.kz">{{ groupOpen.kz ? '▾' : '▸' }}</button>
        </li>

        <template v-if="groupOpen.kz">
          <li v-for="name in KZ_NAMES" :key="name" class="indent">
            <label>
              <input type="checkbox" :checked="isSelected(NAME2CODE[name])"
                @change.stop="toggleKzItem(NAME2CODE[name])" />
              {{ name }}
            </label>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { regionMap } from '../dict.js'
import { FEDERAL_DISTRICTS as DISTRICTS } from '../federal-districts'


const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'Выберите страну или округ' }
})
const emit = defineEmits(['update:modelValue'])

const CODE2NAME = regionMap
const NAME2CODE = Object.fromEntries(
  Object.entries(regionMap).map(([c, n]) => [n, Number(c)])
)

const options = computed(() =>
  Object.entries(CODE2NAME)
    .map(([code, name]) => ({ code: Number(code), name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
)

const selectedCodes = ref([])
const q = ref('')
const listOpen = ref(false)
const foOpen = ref(false)
const inp = ref(null)


const KZ_NAMES = [
  'Кызылординская область', 'Мангистауская область', 'Западно-Казахстанская область', 'Атырауская область',
  'Актюбинская область', 'Акмолинская область', 'Алматинская область', 'Восточно-Казахстанская область',
  'Жамбылская область', 'Карагандинская область', 'Костанайская область', 'Павлодарская область',
  'Северо-Казахстанская область', 'Туркестанская область', 'Город Астана', 'Город Алматы', 'Город Шымкент',
  'Абайская область', 'Жетысуская область', 'Улытауская область'
]

const groupOpen = ref({ ru: true, kz: false })





watch(
  () => props.modelValue,
  v => { selectedCodes.value = (v || []).map(n => Number(n)).filter(n => !Number.isNaN(n)) },
  { immediate: true }
)

const filtered = computed(() => {
  const s = q.value.trim().toLowerCase()
  if (!s) return options.value
  return options.value.filter(o =>
    o.name.toLowerCase().includes(s) || String(o.code).includes(s)
  )
})

const isSelected = code => selectedCodes.value.includes(code)
const toggle = code => {
  if (isSelected(code)) {
    selectedCodes.value = selectedCodes.value.filter(c => c !== code)
  } else {
    selectedCodes.value = [...selectedCodes.value, code]
  }
  emit('update:modelValue', selectedCodes.value)
}
const remove = code => toggle(code)

function onBackspace() {
  if (!q.value && selectedCodes.value.length) {
    remove(selectedCodes.value[selectedCodes.value.length - 1])
  }
}

function onRootClick() {
  if (foOpen.value) return
  listOpen.value = true
  queueMicrotask(() => inp.value?.focus())
}
function closeAll() { listOpen.value = false; foOpen.value = false }
function onDocClick(e) {
  if (!(e.target.closest && e.target.closest('.rsel'))) closeAll()
}
onMounted(() => document.addEventListener('click', onDocClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick))

function toggleFo() {
  foOpen.value = !foOpen.value
  if (foOpen.value) listOpen.value = false
}
function clearFo() { selectedCodes.value = []; emit('update:modelValue', selectedCodes.value) }


const allCodes = computed(() =>
  Object.keys(CODE2NAME).map(n => Number(n)).filter(n => !Number.isNaN(n))
)

const isAllChecked = computed(() =>
  allCodes.value.length > 0 &&
  allCodes.value.every(c => selectedCodes.value.includes(c))
)

function toggleAll(checked) {
  const set = new Set(selectedCodes.value)
  if (checked) {
    allCodes.value.forEach(c => set.add(c))
  } else {
    allCodes.value.forEach(c => set.delete(c))
  }
  selectedCodes.value = Array.from(set)
  emit('update:modelValue', selectedCodes.value)
}













function foCodes(fo) {
  const names = DISTRICTS[fo] || []
  return names.map(n => NAME2CODE[n]).filter(v => v != null)
}

const russiaAllCodes = computed(() => {
  const set = new Set()
  Object.keys(DISTRICTS).forEach(fo => foCodes(fo).forEach(c => set.add(c)))
  return Array.from(set)
})
const kzCodes = computed(() =>
  KZ_NAMES.map(n => NAME2CODE[n]).filter(v => v != null)
)

function isGroupChecked(group) {
  const codes = group === 'ru' ? russiaAllCodes.value : kzCodes.value
  return codes.length && codes.every(c => selectedCodes.value.includes(c))
}

function toggleGroup(group, checked) {
  const codes = group === 'ru' ? russiaAllCodes.value : kzCodes.value
  const set = new Set(selectedCodes.value)
  if (checked) codes.forEach(c => set.add(c))
  else codes.forEach(c => set.delete(c))
  selectedCodes.value = Array.from(set)
  emit('update:modelValue', selectedCodes.value)
}

function toggleKzItem(code) { toggle(code) }

function isFoChecked(fo) {
  const codes = foCodes(fo)
  if (!codes.length) return false
  return codes.every(c => isSelected(c))
}
function toggleFoCheck(fo, checked) {
  const codes = foCodes(fo)
  if (!codes.length) return
  const set = new Set(selectedCodes.value)
  if (checked) {
    codes.forEach(c => set.add(c))
  } else {
    codes.forEach(c => set.delete(c))
  }
  selectedCodes.value = Array.from(set)
  emit('update:modelValue', selectedCodes.value)
}
</script>

<style scoped>
.rsel {
  position: relative;
}

.rsel-field {
  border: 1px solid #ced4da;
  border-radius: .375rem;
  padding: .375rem .5rem;
  min-height: 38px;
  display: flex;
  align-items: center;
  gap: .5rem;
  flex-wrap: wrap;
  background: #fff;
}

.rsel-field.open {
  border-color: #86b7fe;
  box-shadow: 0 0 0 .2rem rgba(13, 110, 253, .25);
}

.rsel-tags {
  display: flex;
  align-items: center;
  gap: .5rem;
  flex: 1;
  flex-wrap: wrap;
}

.tag {
  background: #e7f1ff;
  color: #0d6efd;
  border: 1px solid #cfe2ff;
  padding: .15rem .35rem;
  border-radius: .25rem;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
}

.tag button {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  line-height: 1;
}

.rsel-input {
  border: 0;
  outline: 0;
  min-width: 140px;
  flex: 1;
}

.rsel-fo-btn {
  margin-left: auto;
  white-space: nowrap;
  border: 0;
  background: transparent;
  color: #0d6efd;
}

.rsel-fo-btn:hover {
  text-decoration: underline;
  cursor: pointer;
}

.rsel-dropdown,
.rsel-fo {
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: .375rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, .15);
  max-height: 320px;
  overflow: auto;
  z-index: 1050;
}

.rsel-dropdown {
  right: 0;
}

.rsel-fo {
  width: 360px;
}

.fo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: .5rem .75rem;
  border-bottom: 1px solid #f1f3f5;
}

.btn-link {
  border: 0;
  background: transparent;
  color: #0d6efd;
  cursor: pointer;
}

.list {
  list-style: none;
  padding: .25rem 0;
  margin: 0;
}

.list li {
  padding: .4rem .75rem;
  display: flex;
  align-items: center;
  gap: .6rem;
  cursor: pointer;
}

.list li:hover {
  background: #f8f9fa;
}

.list input[type="checkbox"] {
  pointer-events: none;
}

.name {
  user-select: none;
}

.muted {
  color: #6c757d;
}

.p-2 {
  padding: .5rem;
}

.country {
  background: #f8f9fa;
  border-radius: .375rem;
  margin: .25rem .5rem;
  padding: .35rem .5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.country-label {
  display: flex;
  align-items: center;
  gap: .5rem;
  font-weight: 600;
}

.chevron {
  border: 0;
  background: transparent;
  color: #0d6efd;
  cursor: pointer;
  padding: 0 .25rem;
  font-size: 16px;
  line-height: 1;
}

.indent {
  padding-left: 1.25rem;
  margin-left: .5rem;
  border-left: 2px solid #eef2f6;
}

.divider {
  height: 1px;
  background: #e9ecef;
  margin: .25rem .5rem;
}
</style>
